import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './config/firebase';
import { createAppRoutes } from './router';

const App = () => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setIsLoading(true);

      if (currentUser) {
        setUser(currentUser);

        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

          if (userDoc.exists()) {
            const profile = userDoc.data();
            setUserProfile(profile);

            // Smart redirect based on profile state
            if (location.pathname === '/') {
              if (!profile.role) {
                // No role selected yet - stay on landing to select role
                navigate('/');
              } else if (!profile.onBoardingComplete) {
                // Has role but incomplete profile - go to onboarding
                navigate(`/onboarding/${profile.role}`);
              } else {
                // Complete profile - go to home
                navigate(`/${profile.role}/home`);
              }
            } else if (profile.onBoardingComplete && location.pathname.startsWith('/onboarding/')) {
              // Already complete, shouldn't be on onboarding
              navigate(`/${profile.role}/home`);
            }
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, location.pathname]);

  if ('serviceWorker' in navigator) {
    const swUrl = import.meta.env.DEV ? '/src/firebase-messaging-sw.js' : '/firebase-messaging-sw.js';
    navigator.serviceWorker.register(swUrl, { type: 'module', scope: '/' })
      .then(registration => {
        console.log('Service Worker registered with scope:', registration.scope);
        // Optional: Update check logic
        registration.update();
      })
      .catch(err => {
        console.error('Service Worker registration failed:', err);
      });
  }

  // Foreground notification handling
  useEffect(() => {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notification');
      return;
    }

    console.log('Setting up foreground notification listener...');
    import('firebase/messaging').then(({ getMessaging, onMessage }) => {
      // Pass the initialized app instance
      const messaging = getMessaging(auth.app);
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log('Foreground message received:', payload);
        // Show notification
        const { title, body, route } = payload.data;

        // Simple toast using DOM for now to avoid complex state management in this large file
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-white border-2 border-black p-4 shadow-lg z-50 max-w-md animate-slide-in cursor-pointer hover:bg-gray-50 transition-colors';
        toast.style.animation = 'slideIn 0.3s ease-out';
        toast.innerHTML = `
          <div class="flex items-start space-x-3">
            <div class="flex-1">
              <h4 class="font-bold text-sm mb-1">${title}</h4>
              <p class="text-sm text-gray-600">${body}</p>
            </div>
            <button id="toast-close-btn" class="text-gray-400 hover:text-black p-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
        `;

        // Handle click to navigate
        toast.addEventListener('click', (e) => {
          if (route) {
            navigate(route);
          }
          toast.remove();
        });

        // Handle close button click (stop propagation)
        const closeBtn = toast.querySelector('#toast-close-btn');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            toast.remove();
          });
        }

        document.body.appendChild(toast);

        // Auto remove after 5 seconds
        setTimeout(() => {
          if (document.body.contains(toast)) {
            toast.remove();
          }
        }, 5000);
      });
      return () => unsubscribe();
    }).catch(err => console.log('Messaging not supported or failed to load', err));
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="relative">
            <div className="w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 border-t-2 border-b-2 rounded-full animate-spin" style={{ borderColor: '#C9A961' }}></div>
              <div className="absolute inset-2 border-l-2 border-r-2 rounded-full animate-spin-slow" style={{ borderColor: '#7C3AED' }}></div>
            </div>
          </div>
          <p className="text-xl font-light tracking-widest" style={{ color: '#C9A961' }}>SKILLVEND</p>
          <p className="text-sm font-light tracking-wider mt-2 text-gray-400">Loading Experience</p>
        </div>
      </div>
    );
  }

  // Get routes from configuration
  const routes = createAppRoutes(user, userProfile, navigate);

  return (
    <Routes>
      {routes.map((route, index) => (
        <Route key={index} path={route.path} element={route.element} />
      ))}
    </Routes>
  );
};

export default App;
